import { useCallback, useState } from "react";
import {
  bondDrawCancelRound,
  bondDrawPlayRound,
  fetchBondDrawPlayers,
  fetchBondDrawPool,
} from "../redux/bondDrawSlice";
import {
  buildTicketQuoteFromRound,
  getMatchedNumbers,
} from "../utils/bondDrawHelpers";

const SHUFFLE_MIN_MS = 1400;
const REVEAL_INTERVAL_MS = 1000;
const MATCH_BONUS_MS = 450;
const RESULT_PAUSE_MS = 900;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function useBondDrawPlay(dispatch) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [drawPhase, setDrawPhase] = useState("idle");
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [revealedDrawnCount, setRevealedDrawnCount] = useState(0);
  const [resultModal, setResultModal] = useState(null);

  const resetDrawState = useCallback(() => {
    setDrawnNumbers([]);
    setRevealedDrawnCount(0);
    setDrawPhase("idle");
  }, []);

  const playRound = useCallback(
    async (round) => {
      if (!round?._id || isPlaying) return;
      setIsPlaying(true);
      setDrawnNumbers([]);
      setRevealedDrawnCount(0);
      setDrawPhase("shuffling");

      try {
        const [serverRound] = await Promise.all([
          dispatch(bondDrawPlayRound({ roundId: round._id })).unwrap(),
          delay(SHUFFLE_MIN_MS),
        ]);

        const res = serverRound || round;
        const drawn = Array.isArray(res?.drawNumbers) ? res.drawNumbers : [];
        const selected = Array.isArray(res?.selectedNumbers)
          ? res.selectedNumbers
          : round?.selectedNumbers || [];
        const matched = getMatchedNumbers(selected, drawn);
        const isWin = res?.status === "Won";

        setDrawnNumbers(drawn);
        setDrawPhase("revealing");

        for (let i = 1; i <= drawn.length; i += 1) {
          setRevealedDrawnCount(i);
          const currentBall = drawn[i - 1];
          const isMatch = matched.includes(currentBall);
          if (i < drawn.length) {
            await delay(REVEAL_INTERVAL_MS + (isMatch ? MATCH_BONUS_MS : 0));
          }
        }

        await delay(RESULT_PAUSE_MS);

        const ticket = buildTicketQuoteFromRound(res);
        const grossWin = Number(res?.payoutAmount || 0);
        const netWin =
          res?.playerNetPayout != null
            ? Number(res.playerNetPayout)
            : grossWin > 0
              ? Math.max(0, grossWin - Math.max(0, grossWin - ticket.stake) * 0.05)
              : 0;

        setDrawPhase("result");
        setResultModal({
          isWin,
          matches: matched.length,
          stake: ticket.stake,
          winAmount: grossWin,
          netWinAmount: netWin,
          selectedNumbers: selected,
          drawnNumbers: drawn,
          matchedNumbers: matched,
        });
        setDrawnNumbers([]);
        setRevealedDrawnCount(0);
        setDrawPhase("idle");

        dispatch(fetchBondDrawPool());
        dispatch(fetchBondDrawPlayers({ limit: 100 }));
      } catch (error) {
        resetDrawState();
        throw error;
      } finally {
        setIsPlaying(false);
      }
    },
    [dispatch, isPlaying, resetDrawState]
  );

  const cancelRound = useCallback(
    async (round) => {
      if (!round?._id) return;
      try {
        await dispatch(
          bondDrawCancelRound({ roundId: round._id, stake: Number(round.stake || 0) })
        ).unwrap();
        resetDrawState();
        dispatch(fetchBondDrawPool());
        dispatch(fetchBondDrawPlayers({ limit: 100 }));
      } catch (error) {
        throw error;
      }
    },
    [dispatch, resetDrawState]
  );

  const closeResultModal = useCallback(() => {
    setResultModal(null);
    resetDrawState();
  }, [resetDrawState]);

  return {
    isPlaying,
    drawPhase,
    drawnNumbers,
    revealedDrawnCount,
    resultModal,
    playRound,
    cancelRound,
    closeResultModal,
    resetDrawState,
  };
};
